import React from 'react'
import cx from 'classnames'
import { addStyles, EditableMathField } from 'react-mathquill'
import capitalize from 'lodash.capitalize'

import { NextModal, Button, Tabs, Text, Shortcut, Icon } from '@components/UI'
import { withRenderModal } from '@hocs'
import { Heap } from '@helpers'
import { createEmptyBoard, createFunctionGraph } from '../Sketch/helpers'
import { Formula, Graph } from '../Sketch/CustomElements'

addStyles()

const FormulaGraphToolModal = ({ closeModal, onSave, modalData, canvas }) => {
  const [latex, setLatex] = React.useState(modalData?.latex || '')
  const [activeTab, setActiveTab] = React.useState(
    modalData?.activeTab || 'graph'
  )
  const inputRef = React.useRef()
  const functionGraph = React.useRef()
  const boardRef = React.useRef()
  const axisRef = React.useRef()

  const isEditMode = modalData?.id
  const isUpdating = isEditMode && modalData?.activeTab === activeTab
  const isNew = isEditMode && modalData?.activeTab !== activeTab

  const insertCommand = (command) => () => {
    inputRef.current.cmd(`\\${command}`)
    inputRef.current.focus()
  }

  const deleteGraph = () => {
    if (functionGraph.current) {
      functionGraph.current.remove()
      functionGraph.current.removeAllTicks()
      functionGraph.current = null
    }
  }

  React.useEffect(() => {
    Heap.trigger('Formula Graph Tool - Click - Open')
  }, [])

  React.useEffect(() => {
    if (activeTab === 'graph') {
      const { board, axis } = createEmptyBoard('board')

      boardRef.current = board
      axisRef.current = axis

      return deleteGraph
    }
  }, [activeTab])

  React.useEffect(() => {
    if (activeTab !== 'graph') return

    if (functionGraph.current) {
      deleteGraph()
    }

    boardRef.current.zoom100()
    functionGraph.current = createFunctionGraph(boardRef.current, latex)
  }, [latex, activeTab])

  const createObject = () => {
    const object =
      activeTab === 'graph'
        ? new Graph({ data: latex })
        : new Formula({ data: latex })

    canvas.add(object)
    onSave(object)
    Heap.trigger(`Formula Graph Tool - ${capitalize(activeTab)} - Create`)
  }

  const onClick = () => {
    switch (true) {
      case isUpdating: {
        const object = canvas
          .getObjects()
          .find((object) => object.id === modalData.id)

        object.set('data', latex)
        object.fire('modified', { data: latex })
        onSave(object)
        Heap.trigger(`Formula Graph Tool - ${capitalize(activeTab)} - Update`)
        break
      }

      case isNew: {
        const object = canvas
          .getObjects()
          .find((object) => object.id === modalData.id)

        canvas.remove(object)
        createObject()
        break
      }

      default:
        createObject()
    }

    canvas.renderAll()
    deleteGraph()
    closeModal()
  }

  return (
    <NextModal
      className={cx({
        fullscreen: true,
        'gp-math-modal': true,
        'auto-height': activeTab === 'formula'
      })}
      close={closeModal}
      withHeader={false}
      withFooter={false}
    >
      <Tabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={[
          { id: 'graph', title: 'Graph' },
          { id: 'formula', title: 'Formula' }
        ]}
      >
        <div className='relative-container'>
          {activeTab === 'graph' && <div id='board' className='canvas' />}

          <div className='content-container'>
            <div className='math-container'>
              {activeTab === 'graph' && <Text className='fx'>f(x) = </Text>}

              <EditableMathField
                style={
                  activeTab === 'graph' ? { paddingLeft: 56 } : { height: 133 }
                }
                latex={latex}
                mathquillDidMount={(mathField) =>
                  (inputRef.current = mathField)
                }
                onChange={(field) => setLatex(field.latex())}
              />

              <div
                className={cx({
                  'quick-access-container': true,
                  'align-bottom': activeTab === 'formula'
                })}
              >
                <button onClick={insertCommand('sqrt')}>
                  <Icon name='sqrt' fill='white' />
                </button>
                <button onClick={insertCommand('infty')}>
                  <Icon name='infinity' fill='white' />
                </button>
                <button onClick={insertCommand('pm')}>
                  <Icon name='plus-minus' fill='white' />
                </button>
                <button onClick={insertCommand('pi')}>
                  <Icon name='pi' fill='white' />
                </button>
                <button onClick={insertCommand('sum')}>
                  <Icon name='sum' fill='white' />
                </button>
                <button onClick={insertCommand('triangle')}>
                  <Icon name='triangle' fill='white' />
                </button>
              </div>
            </div>

            <Text className='text fluid'>
              {activeTab === 'graph'
                ? 'Use x as a variable, and only valid math, to be able to plot graphs. Example: sin(x)+2'
                : 'Full LaTeX commands are supported (e.g. pi, sqrt, integral).'}
            </Text>

            <div className='footer'>
              <div className='instructions'>
                {activeTab === 'formula' && (
                  <React.Fragment>
                    <Text>Use</Text>
                    <Shortcut meta='/' />
                    <Text>for fractions and</Text>
                    <Shortcut meta='^' />
                    <Text>for zomm in and out.</Text>
                  </React.Fragment>
                )}

                {activeTab === 'graph' && (
                  <React.Fragment>
                    <Text>Hold</Text>
                    <Shortcut meta='&#8679;' hint='Drag' />
                    <Text>to reposition. Hold</Text>
                    <Shortcut meta='&#8679;' hint='Scroll' />
                    <Text>to zoom in and out.</Text>
                  </React.Fragment>
                )}
              </div>

              <Button
                theme='b3 inverted'
                text={`${isUpdating ? 'Update' : 'Insert'} ${activeTab}`}
                events={{ onClick }}
              />
            </div>
          </div>
        </div>
      </Tabs>
    </NextModal>
  )
}

export default withRenderModal('formula-graph-modal')(FormulaGraphToolModal)
